import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import moment from 'moment';
import _ from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { UseSnCode } from 'utils/useSnCode';
import { forceTextOnDateCells } from 'utils/excel.utils';

interface ExportProps {
    data: any[];
    filter: any;
    t: any;
    checkNumber: (value: any) => boolean;
    checkNull: (value: any) => any;
    toBuddhistYear: (momentObj: moment.Moment, format: string) => string;
}

const exportPatientListToExcel = async (props: ExportProps) => {
    const sn = await UseSnCode()

    const { data, filter, t, checkNumber, checkNull, toBuddhistYear } = props;
    const headCells = [
        'REPORT.TABLE.PATIENT.#NO',
        'REPORT.TABLE.PATIENT.PATIENT_CN_NUMBER',
        'REPORT.TABLE.PATIENT.PATIENT_BRANCH_CN_NUMBER',
        'REPORT.TABLE.PATIENT.PATIENT_SN_CODE',
        'REPORT.TABLE.PATIENT.PATIENT_NAME',
        'REPORT.TABLE.PATIENT.ID_CARD',
        'REPORT.TABLE.PATIENT.GENDER',
        'REPORT.TABLE.PATIENT.DATE_OF_BIRTH',
        'REPORT.TABLE.PATIENT.AGE',
        'REPORT.TABLE.PATIENT.ETHNICITY',
        'REPORT.TABLE.PATIENT.NATIONALITY',
        'REPORT.TABLE.PATIENT.RELIGION',
        'REPORT.TABLE.PATIENT.MARITAL',
        'REPORT.TABLE.PATIENT.OCCUPATION',
        'REPORT.TABLE.PATIENT.CHANNEL',
        'REPORT.TABLE.PATIENT.PATIENT_TYPE',
        'REPORT.TABLE.PATIENT.BLOOD_TYPE',
        'REPORT.TABLE.PATIENT.DISEASE',
        'REPORT.TABLE.PATIENT.MEDICINE',
        'REPORT.TABLE.PATIENT.ALLERGIC',
        'REPORT.TABLE.PATIENT.SYMPTOMS',
        'REPORT.TABLE.PATIENT.TREATMENT_RIGHT',
        'REPORT.TABLE.PATIENT.ADDRESS',
        'REPORT.TABLE.PATIENT.MOBILE',
        'REPORT.TABLE.PATIENT.TEL',
        'REPORT.TABLE.PATIENT.LINE',
        'REPORT.TABLE.PATIENT.EMAIL',
        'REPORT.TABLE.PATIENT.FACEBOOK',
    ];

    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet(t(`REPORT.REPORT_NAME.PATIENT_INFORMATION`));
    const reportNameRow = sheet.addRow([t(`REPORT.REPORT_NAME.PATIENT_INFORMATION`)]);
    reportNameRow.font = { bold: true, size: 16 };
    sheet.mergeCells(`A1:${String.fromCharCode(64 + headCells.length)}1`);
    reportNameRow.alignment = { horizontal: 'center' };

    const filterInfoRow = sheet.addRow([
        `${t('REPORT.FILTER.BRANCH')}: ${filter.branch || '-'}`,
    ]);
    filterInfoRow.font = { size: 12 };
    sheet.mergeCells(`A2:${String.fromCharCode(64 + headCells.length)}2`);
    const headerRow = sheet.addRow(headCells.map((headCell) => t(headCell)));
    headerRow.font = { bold: true, size: 12 };
    headerRow.alignment = { horizontal: 'center' };

    headCells.forEach((headCell: any, index: number) => {
        const cell = headerRow.getCell(index + 1);
        cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
        };
    });

    let currentRow = 3;

    for (let i = 0; i < data.length; i++) {
        const item: any = data[i];

        const rowValues = [
            currentRow - 2,
            item.cnNumber || '-',
            item.branchCnNumber || '-',
            item.snCode || '-',
            `${item.prefixName || ''}${item.firstname || ''} ${item.lastname || ''}`,
            checkNull(item.idCard),
            t(`REPORT.VALUE.PATIENT.GENDER.${item.gender}`) || '-',
            item.dateOfBirth ? toBuddhistYear(moment(item.dateOfBirth), 'DD/MM/YYYY') : t(`REPORT.VALUE.PATIENT.BLOOD_TYPE.NOT_SPECIFIED`),
            item.age || '-',
            item.originName || '-',
            item.nationalityName || '-',
            t(`REGISTER.FORM.RELIGION_OPTION.${item.religion}`) || '-',
            t(`REGISTER.FORM.MARITAL_OPTION.${item.maritalStatus}`) || '-',
            item.careerName || '-',
            item.accessChannel || '-',
            item.patientTypeName || '-',
            item.bloodType === 'NOT_SPECIFIED' ? t(`REPORT.VALUE.PATIENT.BLOOD_TYPE.NOT_SPECIFIED`) : item.bloodType,
            item.disease || '-',
            item.medicineUse || '-',
            item.beAllergic || '-',
            item.importantSymptoms || '-',
            item.groupTreatmentRight || '-',
            item.fullAddress || '-',
            checkNull(item.phone),
            checkNull(item.telephone),
            item.line || '-',
            item.email || '-',
            item.facebook || '-',
        ];

        const dataRow = sheet.addRow(rowValues);
        dataRow.height = 20; // Set row height here
        dataRow.eachCell((cell) => {
            cell.border = {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' }
            };
        });

        currentRow++;
    }

    for (let i = 1; i <= headCells.length; i++) {
        sheet.getColumn(i).width = 20;
        sheet.getColumn(i).alignment = { vertical: 'top', horizontal: 'left' };
        if (i === 1) {
            sheet.getColumn(i).alignment = { vertical: 'top', horizontal: 'center' };
        }
    }
    if (!sn) {
        sheet.spliceColumns(4, 1);
    }
    forceTextOnDateCells(workbook);
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `${t(`REPORT.REPORT_NAME.PATIENT_INFORMATION`)}.xlsx`);
};

export default exportPatientListToExcel;
